import React, { useState } from "react";
import { useFormik } from "formik";
import { useNavigate, useLocation } from "react-router-dom";
import * as Yup from "yup";
import QRCode from "qrcode.react";
import { Button, AlertMessage } from "components/atoms";
import { ReadOnlyInput, TextInput } from "components/molecules";
import { useAuth } from "contexts";
// import { ProfileHandling } from "services";
import { fetchAuthSession } from "aws-amplify/auth";

const SetupTotpSchema = Yup.object().shape({
  totpCode: Yup.string()
    .max(6, "TOTP code must be 6 characters")
    .required("TOTP code is required"),
});

const SetupTotpForm: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { authState, setAuthState, handleSignInConfirmation } = useAuth();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const handleSuccessfulSetup = (redirectPath?: string): void => {
    setupTotpFormik.resetForm();

    if (redirectPath) {
      navigate(redirectPath, { replace: true });
    } else {
      navigate("/", { replace: true });
    }
  };

  const handleSetupError = (error: unknown): void => {
    const errorMessage = error instanceof Error ? error.message : String(error);
    
    if (errorMessage.includes("Access Denied.")) {
      setErrorMessage("Your access is either invalid or has not yet been confirmed. Please reach out to your system administrator for support.");
    } else {
      setErrorMessage(errorMessage);
    }
  };

  // const createUserProfile = async (userData: any): Promise<void> => {
  //   try {
  //     const existingUser: any = await ProfileHandling.fetchUser(
  //       userData.id,
  //       "userPool"
  //     );

  //     if (!existingUser?.data?.getUser) {
  //       await ProfileHandling.createUser(userData);
  //     }
  //   } catch (error) {
  //     if (
  //       error instanceof Error &&
  //       (error.message.includes("not found") ||
  //         error.message.includes("not exist"))
  //     ) {
  //       try {
  //         await ProfileHandling.createUser(userData);
  //       } catch (createError) {
  //         throw createError;
  //       }
  //     } else {
  //       throw error;
  //     }
  //   }
  // };

  const handleSetupTotpSubmit = async (values: { totpCode: string }) => {
    try {
      const { redirectAfterSignUp } = location.state || {};

      const confirmSignInError = await handleSignInConfirmation(
        values.totpCode.toString()
      );

      if (confirmSignInError) {
        handleSetupError(confirmSignInError);
        return;
      }

      const idToken = (await fetchAuthSession()).tokens?.idToken;
      if (!idToken?.payload) {
        throw new Error("ID token or payload is missing");
      }

      handleSuccessfulSetup(redirectAfterSignUp);
    } catch (error) {
      handleSetupError(error);
    }
  };

  const setupTotpFormik = useFormik({
    initialValues: {
      totpCode: "",
    },
    validationSchema: SetupTotpSchema,
    onSubmit: handleSetupTotpSubmit,
  });

  const toSignIn = (): void => {
    setAuthState((prevState) => ({
      ...prevState,
      nextStep: "SIGN_IN",
    }));
  };

  return (
    <form className="auth-form" onSubmit={setupTotpFormik.handleSubmit}>
      <div className="form-group">
        <h2>Time-based One-Time Password (TOTP) Setup</h2>
        <small>
          To set up TOTP, download a free authenticator app (like Google
          Authenticator or Microsoft Authenticator) from your phone's{" "}
          <b>app store</b>. Open the app, scan the QR code, and enter the
          verification code.
        </small>
      </div>
      <div className="form-qrcodeImage">
        <QRCode
          value={authState?.temporaryData?.setupUri || ""}
          size={250}
          level={"H"}
          includeMargin={true}
        />
      </div>

      <ReadOnlyInput
        id="shared"
        value={authState?.temporaryData?.sharedSecret || ""}
        label="Shared Secret"
      />

      <TextInput
        label="Code *"
        id="totpCode"
        placeholder="Enter the code"
        inputMode="numeric"
        maxLength={6}
        getFieldProps={setupTotpFormik.getFieldProps}
        touched={setupTotpFormik.touched.totpCode}
        error={setupTotpFormik.errors.totpCode}
      />

      {errorMessage && <AlertMessage type="error">{errorMessage}</AlertMessage>}

      <Button
        type="submit"
        text="Confirm"
        className="form-button"
        width="100%"
        variant="primary"
      />

      <Button
        type="button"
        text="Back to Sign In"
        onClick={toSignIn}
        className="form-button"
        width="100%"
        variant="border"
      />
    </form>
  );
};

export default SetupTotpForm;
